import React from 'react';
import { Immutable } from 'nuclear-js';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import DataUtils from 'data/utils';
import BatchPrintUtils from '../utils';
import FormattingUtils from 'utils/formatting';
import RuntimeActions from 'data/app-runtime/actions';
import Clients from 'http-clients';
import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import CommonStrings from 'resources/common-strings';

import Navigation from './navigation';
import Content from './content';
import Summary from './summary';
import Success from './success';
import Failure from './failure';

export default CreateReactClass({
    displayName: 'batch-options',

    PropTypes: {
        batchTab: PropTypes.oneOf(['subjects', 'pages', 'students']).isRequired,
        displayPages: PropTypes.instanceOf(Immutable.List).isRequired,
        batchOptionState: PropTypes.bool.isRequired,
        isFamilyReport: PropTypes.bool.isRequired,
        isSingleStudentMode: PropTypes.bool.isRequired,
        linkingStudies: PropTypes.instanceOf(Immutable.List).isRequired,
        nextButtonAction: PropTypes.func.isRequired,
        reportQueueLink: PropTypes.string.isRequired,
        selectFormat: PropTypes.func.isRequired,
        selectStudents: PropTypes.func.isRequired,
        selectedClass: PropTypes.instanceOf(Immutable.Map).isRequired,
        selectedModule: PropTypes.instanceOf(Immutable.Map).isRequired,
        selectedPrintFormat: PropTypes.instanceOf(Immutable.Set).isRequired,
        selectedStudentForPrinting: PropTypes.oneOf(['previous', 'current', 'next']).isRequired,
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        selectedTermNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        studentInfo: PropTypes.instanceOf(Immutable.List),
        studentPreviousSelection: PropTypes.oneOf(['previous', 'current', 'next']).isRequired,
        students: PropTypes.instanceOf(Immutable.List).isRequired,
        termsWithTestEvents: PropTypes.instanceOf(Immutable.List),
        togglePrintAndShare: PropTypes.func.isRequired,
        togglePrintFormat: PropTypes.func.isRequired,
        toggleStudentSelection: PropTypes.func.isRequired,
        visitedSections: PropTypes.instanceOf(Immutable.Set).isRequired,
    },

    getInitialState() {
        return {
            pagesChanged: false,
            selectedGroupByOption: CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC,
            selectedDataForPrinting: this.props.linkingStudies,
            selectedPagesForPrinting: Immutable.Set(['profile']),
            selectedPrintFormat: Immutable.Set(),
            selectedStudentsOption: 'current',
            selectedStudents: Immutable.Set(),
            selectedStudentBids: Immutable.Set(),
            selectedSubjectBidsForPrinting: Immutable.Set(),
            selectedSubjectsForPrinting: Immutable.Set(),
            studentsChanged: false,
            subjectsChanged: false,
        };
    },

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!nextProps.batchOptionState) {
            this.reset();
        }
    },

    reset() {
        this.setState(this.getInitialState());
    },

    selectSubjects(subjects, subjectBids) {
        this.setState({
            selectedSubjectBidsForPrinting: subjectBids,
            selectedSubjectsForPrinting: subjects,
            subjectsChanged: true,
        });
    },

    selectData(dataToPrint) {
        this.setState({
            selectedDataForPrinting: dataToPrint,
        });
    },

    selectPages(pages) {
        this.setState({
            selectedPagesForPrinting: pages,
            pagesChanged: true,
        });
    },

    selectGroupBy(groupBy) {
        this.setState({
            selectedGroupByOption: groupBy,
        });
    },

    selectStudents(students) {
        this.setState({
            selectedStudentsOption: students,
        });
        this.props.selectStudents(students);
    },

    onSelectStudents(students, studentBids) {
        this.setState({
            selectedStudents: students,
            selectedStudentBids: studentBids,
            studentsChanged: true,
        });
    },

    nextButtonAction(event, source) {
        if (DataUtils.canPerformNextButtonAction(this.props, this.state)) {
            this.props.nextButtonAction(event, source);
        }
    },

    setClearSelectedStudents(callback) {
        this.setState({
            clearSelectedStudents: callback,
        });
    },

    createPdfAction(event, callCount = 0) {
        Analytics.recordButtonClick(CommonValues.SectionIDs.printAndShare, 'create-batch-pdf');

        const payload = BatchPrintUtils.constructBatchPrintPayload(this.state, this.props);
        let alertMessage;
        const callback = (data) => {
            let actionStatus;
            if (data) {
                alertMessage = <Success reportQueueLink={this.props.reportQueueLink} />;
                this.props.togglePrintAndShare(event, CommonStrings.batchPrint.printStatus.success);
                actionStatus = CommonStrings.batchPrint.printStatus.success;
            } else {
                alertMessage = <Failure />;
                actionStatus = CommonStrings.batchPrint.printStatus.failure;
            }
            RuntimeActions.setPageAlert(
                Immutable.Map({
                    messageContent: alertMessage,
                    isActive: true,
                    actionStatus: actionStatus,
                })
            );
        };
        const failureCallback = (error) => {
            console.error('postBatchPrintRequest error => ', error);
            if (callCount <= 1) {
                callCount = callCount + 1;
                return this.createPdfAction(event, callCount);
            } else {
                if (this.props.isFamilyReport) {
                    this.props.togglePrintAndShare(event, CommonStrings.batchPrint.printStatus.failure);
                }
                alertMessage = <Failure />;
                RuntimeActions.setPageAlert(
                    Immutable.Map({
                        messageContent: alertMessage,
                        isActive: true,
                        actionStatus: CommonStrings.batchPrint.printStatus.failure,
                    })
                );
                return false;
            }
        };
        return Clients.batchPrintApi.postBatchPrintRequest(payload, callback, failureCallback);
    },

    render() {
        const subjectName = this.props.selectedSubject ? this.props.selectedSubject.get('description') : '';
        const subjectBid = this.props.selectedSubject ? this.props.selectedSubject.get('bid') : '';

        const selectedModule = this.props.selectedModule ? this.props.selectedModule : 'profile';
        const selectedStudents = this.props.students.map((s) => FormattingUtils.studentFullNameWithInitial(s)).toSet();
        const selectedStudentBids = this.props.students.map((s) => s.get('studentBid')).toSet();

        return (
            <div className="print-batch-options">
                <Navigation
                    batchTab={this.props.batchTab}
                    isFamilyReport={this.props.isFamilyReport}
                    isFamilyReportURL={this.props.isFamilyReportURL}
                    nextButtonAction={this.nextButtonAction}
                    pagesChanged={this.state.pagesChanged}
                    selectedModule={selectedModule}
                    toggleStudentSelection={this.props.toggleStudentSelection}
                    selectedPages={this.state.selectedPagesForPrinting}
                    selectedStudentForPrinting={this.props.selectedStudentForPrinting}
                    selectedStudentsFromList={this.state.selectedStudents}
                    clearSelectedStudents={this.props.clearSelectedStudents}
                    selectedSubjects={this.state.selectedSubjectsForPrinting}
                    students={this.props.students}
                    studentsChanged={this.state.studentsChanged}
                    subjectsChanged={this.state.subjectsChanged}
                    visitedSections={this.props.visitedSections}
                />
                <Content
                    dropDowns={this.props.dropDowns}
                    classSelected={this.props.classSelected}
                    batchOptionState={this.props.batchOptionState}
                    createPdfAction={this.createPdfAction}
                    displayPages={this.props.displayPages}
                    isFamilyReport={this.props.isFamilyReport}
                    isFamilyReportURL={this.props.isFamilyReportURL}
                    isHelpBoxOpen={this.props.isHelpBoxOpen}
                    isSingleStudentMode={this.props.isSingleStudentMode}
                    linkingStudies={this.props.linkingStudies}
                    nextButtonAction={this.nextButtonAction}
                    onSelectStudents={this.onSelectStudents}
                    selectSubjects={this.selectSubjects}
                    selectedGroupByOption={this.state.selectedGroupByOption}
                    selectedModule={selectedModule}
                    selectedPrintFormat={this.props.selectedPrintFormat}
                    selectedStudents={selectedStudents}
                    selectedStudentBids={selectedStudentBids}
                    selectedStudentForPrinting={this.props.selectedStudentForPrinting}
                    selectedStudentsFromList={this.state.selectedStudents}
                    selectData={this.selectData}
                    selectFormat={this.props.selectFormat}
                    selectGroupBy={this.selectGroupBy}
                    selectPages={this.selectPages}
                    selectStudents={this.selectStudents}
                    setClearSelectedStudents={this.props.setClearSelectedStudents}
                    subjectActive={subjectName}
                    subjectBid={subjectBid}
                    students={this.props.students}
                    studentPreviousSelection={this.props.studentPreviousSelection}
                    studentsChanged={this.state.studentsChanged}
                    togglePrintAndShare={this.props.togglePrintAndShare}
                    togglePrintFormat={this.props.togglePrintFormat}
                    toggleStudentSelection={this.props.toggleStudentSelection}
                />
                <Summary
                    displayPages={this.props.displayPages}
                    isFamilyReport={this.props.isFamilyReport}
                    pageActive={selectedModule}
                    pagesChanged={this.state.pagesChanged}
                    selectedTermNumber={this.props.selectedTermNumber}
                    selectedClass={this.props.selectedClass}
                    selectedData={this.state.selectedDataForPrinting}
                    selectedGroupByOption={this.state.selectedGroupByOption}
                    selectedPages={this.state.selectedPagesForPrinting}
                    selectedPrintFormat={this.props.selectedPrintFormat}
                    selectedStudentForPrinting={this.props.selectedStudentForPrinting}
                    selectedStudents={this.state.selectedStudents}
                    selectedStudentsOption={this.state.selectedStudentsOption}
                    selectedSubjects={this.state.selectedSubjectsForPrinting}
                    studentInfo={this.props.studentInfo}
                    students={this.props.students}
                    studentsChanged={this.state.studentsChanged}
                    subjectActive={subjectName}
                    subjectsChanged={this.state.subjectsChanged}
                    termsWithTestEvents={this.props.termsWithTestEvents}
                    visitedSections={this.props.visitedSections}
                />
            </div>
        );
    },
});
